import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import {
  captureMobileException,
  isServerError,
} from '@south-street-app/configs';

import { BackendInternalErrorProvider } from '../backend-internal-error/BackendInternalErrorProvider';

const QueryProvider = ({ children }: { children: ReactNode }) => {
  const [isError, setIsError] = useState(false);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            console.error('An error occurred:', error);

            if (isServerError(error)) {
              captureMobileException(error, { area: 'react-query' });

              setIsError(true);
            }
          },
          onSuccess: () => {
            setIsError(false);
          },
        }),
      }),
  );

  useEffect(() => {
    if (!isError) {
      return;
    }

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const hasServerError = queryClient
        .getQueryCache()
        .getAll()
        .some((query) => {
          const error = query.state.error;

          return isServerError(error);
        });

      if (!hasServerError) {
        setIsError(false);
      }
    });

    return unsubscribe;
  }, [isError, queryClient]);

  const onRefetch = useCallback(() => {
    queryClient.refetchQueries();
  }, [queryClient]);

  return (
    // Provide the client to your App
    <QueryClientProvider client={queryClient}>
      <BackendInternalErrorProvider isError={isError} onRefetch={onRefetch}>
        {children}
      </BackendInternalErrorProvider>
    </QueryClientProvider>
  );
};

export { QueryProvider };
